import { V2, hoverOnly } from '../../theme/tokens'
import { v2Tone } from '../../theme/theme'
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  HelpOutline,
  Info as InfoIcon,
  MoreVert,
  AddCircleOutline,
  Block,
  SwapHoriz,
  RestartAlt,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'sonner';
import KpiRow, { KpiCell } from '../../components/common/KpiRow';
import StatTile from '../../components/common/StatTile';
import { DonutCard } from '../../components/dashboard/DashboardKit';
import { formatDate, formatInt, formatPercent } from '../../utils/format';
import DisabledReason from '../../components/common/DisabledReason';
import KpiCardsSkeleton from '../../Skeletons/KpiCardsSkeleton';
import CardListSkeleton from '../../Skeletons/CardListSkeleton';
import TableSkeleton from '../../Skeletons/TableSkeleton';

// 🔴 K-3: `RelationTypeCard` bekommt seinen Ton als Prop (`info`/`warning`/
// `success`). `info` und `warning` sind in `theme.js` NICHT gebunden — MUI
// liefert `palette.<ton>.main`, hell 3,857:1 bzw. 3,113:1 auf dem Papier. Wo
// der Ton SCHRIFT ist (Kartenkopf, umrandeter Chip), wird er auf die v2-Skala
// gezogen; `success` bleibt bei MUI.
// 🔴 Steht NACH dem Importblock, nicht darin: `import/first` (ESLint, Rule 9)
// verbietet jede Anweisung zwischen den Importen — die erste Fassung hatte die
// Konstante oben stehen und loeste 13 Fehler aus.
const TON_ZU_V2 = { info: 'info', warning: 'warn' }

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || '';

const DECISION_LABELS = {
  force_complement: 'Complement',
  force_addon: 'Add-on',
  force_competitor: 'Competitor',
  exclude: 'Excluded',
  approve: 'Approved',
};

const DECISION_COLORS = {
  force_complement: 'success',
  force_addon: 'info',
  force_competitor: 'warning',
  exclude: 'error',
  approve: 'default',
};

// setSnackbar shim — module-scoped identity. Kept the same { message, severity }
// shape so existing call sites (setSnackbar({message, severity})) work unchanged.
function setSnackbar(next) {
  if (!next) return;
  const { severity = 'info', message } = next;
  const fn = severity === 'success' ? toast.success
    : severity === 'error' ? toast.error
    : severity === 'warning' ? toast.warning
    : toast.info;
  fn(message || '');
}

export default function RelationsManagerTab() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('summary');
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [healthStats, setHealthStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  // Override state
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(null); // null | 'complement' | 'addon' | 'competitor'

  // Load health stats and products on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const productsRes = await axios.get(`${API_BASE_URL}/api/products`);
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        setStatsLoading(true);
        const statsRes = await axios.get(`${API_BASE_URL}/api/relations/health-stats`);
        setHealthStats(statsRes.data);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    loadData();
  }, []);

  // Fetch summary + overrides when product selected
  useEffect(() => {
    if (selectedProduct) {
      fetchSummary(selectedProduct.product_id);
      fetchOverrides(selectedProduct.product_id);
    } else {
      setSummaryData(null);
      setOverrides([]);
    }
  }, [selectedProduct]);

  const fetchSummary = async (productId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/relations/summary/${productId}`);
      setSummaryData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load relation summary');
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async (productId) => {
    setOverridesLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/relations/overrides/${productId}`,
        { headers: { 'X-Admin-Key': ADMIN_KEY } }
      );
      setOverrides(res.data.overrides || []);
    } catch (err) {
      console.error('Failed to fetch overrides:', err);
    } finally {
      setOverridesLoading(false);
    }
  };

  const saveOverride = useCallback(async (candidateId, decision, note = '') => {
    if (!selectedProduct) return;
    setSavingOverride(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/relations/bulk-override`,
        {
          anchor_product_id: selectedProduct.product_id,
          operations: [{ candidate_product_id: candidateId, decision, note }],
        },
        { headers: { 'X-Admin-Key': ADMIN_KEY } }
      );
      await fetchOverrides(selectedProduct.product_id);
      const label = decision === 'auto' ? 'Override removed' : `Saved as ${DECISION_LABELS[decision] || decision}`;
      setSnackbar({ message: label, severity: 'success' });
    } catch (err) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to save override', severity: 'error' });
    } finally {
      setSavingOverride(false);
    }
  }, [selectedProduct]);

  // Build a lookup map for fast override checks
  const overrideByCandidate = Object.fromEntries(
    overrides.map((o) => [o.candidate_product_id, o])
  );

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Relations Manager
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            View and manage product relationships, review top recommendations, and track quality metrics.
          </Typography>
        </Box>
        <Tooltip title="Show help information">
          <IconButton onClick={() => setShowHelp(!showHelp)} size="small">
            <HelpOutline />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Help Section */}
      <Collapse in={showHelp}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            About Product Relations
          </Typography>
          <Typography variant="body2" paragraph>
            Product relations are automatically generated based on taxonomy affinity, shared attributes, and compatibility rules.
            There are three types of relations:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
            <li><strong>Complements:</strong> Products that work well together (e.g., espresso machine + grinder)</li>
            <li><strong>Add-ons:</strong> Accessories and extras (e.g., cleaning supplies, filters, cups)</li>
            <li><strong>Competitors:</strong> Alternative products in the same category (e.g., similar espresso machines)</li>
          </Box>
          <Typography variant="body2">
            Use the <strong>"⋮" menu</strong> on any relation to exclude it, change its type, or reset it to automatic.
            Use <strong>"+ Add"</strong> to manually pin any product to a relation type.
            Manual changes take effect immediately in the override list and after the next nightly batch run in the storefront.
          </Typography>
        </Alert>
      </Collapse>

      {/* Health Stats */}
      {statsLoading ? (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : healthStats && (
        <HealthStatsCard stats={healthStats} />
      )}

      {/* Product Selector */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Autocomplete
          options={products}
          getOptionLabel={(option) => `${option.product_name} (${option.sku})`}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase();
            if (!inputValue) return options.slice(0, 100);
            return options.filter(
              (option) =>
                option.product_name?.toLowerCase().includes(inputValue) ||
                option.sku?.toLowerCase().includes(inputValue)
            ).slice(0, 100);
          }}
          value={selectedProduct}
          onChange={(event, newValue) => setSelectedProduct(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Product"
              placeholder="Search by name or SKU…"
              variant="outlined"
            />
          )}
          sx={{ maxWidth: 600 }}
          loading={products.length === 0}
        />
      </Box>

      {/* View Mode Toggle */}
      {selectedProduct && (
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="summary">
              <ViewModule sx={{ mr: 1 }} />
              Summary
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList sx={{ mr: 1 }} />
              List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Loading State — K-S (UI-Durchgang 2): Geruest der geladenen
          Beziehungs-Flaeche statt Kreisel. 🔴 Nachbesserung 3 (Regel-16-WV1
          B-1): das Geruest haengt jetzt an `viewMode` und beginnt in der
          Zusammenfassung mit der ANKER-KARTE. Bis dahin fehlten beide — die
          Kachelreihe und die Karten rueckten beim Eintreffen der Daten um die
          Hoehe der Anker-Karte (plus `mb: 3`) nach unten, und in der
          Listenansicht stand ein Kartenstapel ueber einer Tabelle.
          Zusammenfassung (`SummaryView`, die Vorgabeansicht):
            * die Anker-Karte (`:409-416`) — zwei Textzeilen in einer MUI-`Card`,
              VOR der Kachelreihe,
            * drei Kacheln — `KpiRow` mit drei `StatTile` (`:419-434`),
            * drei Karten — die drei `RelationTypeCard` (`:451`, `:462`, `:473`),
              ebenfalls MUI-`Card` (`:518`) — daher `form="card"` fuer beide
              Kartenflaechen.
          Listenansicht (`ListView`): eine Tabelle in
          `TableContainer component={Paper}` (`:855`) mit SECHS Kopfzellen
          (`:859-864`) — daher `columns={6}` und `paper="elevation"` (schlichtes
          MUI-`Paper`, nicht `Panel` und nicht `variant="outlined"`).
          🔴 UNTERGRENZE, wie in `RMAReports`: die bedingten Bloecke zwischen
          Kachelreihe und Karten (`Alert` `:436-440`, Cache-Zeile `:442-446`)
          reserviert das Geruest nicht — sie erscheinen nur mit Daten. */}
      {loading && (
        viewMode === 'list' ? (
          <TableSkeleton
            columns={6}
            rows={5}
            paper="elevation"
            ariaLabel="Loading relations"
          />
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <CardListSkeleton
                cards={1}
                lines={2}
                form="card"
                ariaLabel="Loading product anchor"
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <KpiCardsSkeleton cards={3} ariaLabel="Loading relation metrics" />
            </Box>
            <CardListSkeleton cards={3} lines={3} form="card" ariaLabel="Loading relations" />
          </>
        )
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary View */}
      {!loading && viewMode === 'summary' && summaryData && (
        <SummaryView
          data={summaryData}
          overrideByCandidate={overrideByCandidate}
          overridesLoading={overridesLoading}
          onOverride={saveOverride}
          onAddProduct={(type) => setAddDialogOpen(type)}
        />
      )}

      {/* List View */}
      {!loading && viewMode === 'list' && summaryData && (
        <ListView
          data={summaryData}
          overrideByCandidate={overrideByCandidate}
          onOverride={saveOverride}
        />
      )}

      {/* Manual Overrides Section */}
      {!loading && selectedProduct && (
        <ManualOverridesSection
          overrides={overrides}
          loading={overridesLoading}
          onReset={(candidateId) => saveOverride(candidateId, 'auto')}
          saving={savingOverride}
        />
      )}

      {/* Empty State */}
      {!loading && !selectedProduct && (
        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
          <Typography variant="h6">Select a product to view relations</Typography>
        </Box>
      )}

      {/* Add Product Dialog */}
      <AddProductDialog
        open={addDialogOpen !== null}
        relationType={addDialogOpen}
        products={products}
        anchorProduct={selectedProduct}
        onClose={() => setAddDialogOpen(null)}
        onSave={(candidateId, decision, note) => {
          saveOverride(candidateId, decision, note);
          setAddDialogOpen(null);
        }}
        saving={savingOverride}
      />

    </Box>
  );
}

// ---------------------------------------------------------------------------
// SummaryView
// ---------------------------------------------------------------------------

function SummaryView({ data, overrideByCandidate, overridesLoading, onOverride, onAddProduct }) {
  const { anchor, top_complements, top_addons, top_competitors, stats, cache_age_seconds, note } = data;

  return (
    <Box>
      {/* Anchor Product Info */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography variant="h6">{anchor.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            SKU: {anchor.sku} | Vendor: {anchor.vendor}
          </Typography>
        </CardContent>
      </Card>

      {/* Stats Summary — KpiRow/KpiCell statt Card-Reihe (pattern-library: Kennzahlenstreifen) */}
      <KpiRow sx={{ mb: 3 }}>
        <KpiCell>
          <StatTile label="Total Relations" value={formatInt(stats.total_relations)} />
        </KpiCell>
        <KpiCell>
          <StatTile label="Overrides" value={formatInt(stats.override_count)} />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Approval Rate"
            value={stats.approval_rate !== null && stats.approval_rate !== undefined
              ? formatPercent(stats.approval_rate, { digits: 0 })
              : '—'}
          />
        </KpiCell>
      </KpiRow>

      {note && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {note}
        </Alert>
      )}

      {cache_age_seconds !== null && cache_age_seconds !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Cache age: {Math.round(cache_age_seconds)}s
        </Typography>
      )}

      {/* Top Relations by Type */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <RelationTypeCard
            title="Top Complements"
            relations={top_complements}
            color="success"
            relationType="complement"
            overrideByCandidate={overrideByCandidate}
            onOverride={onOverride}
            onAdd={() => onAddProduct('complement')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <RelationTypeCard
            title="Top Add-ons"
            relations={top_addons}
            color="info"
            relationType="addon"
            overrideByCandidate={overrideByCandidate}
            onOverride={onOverride}
            onAdd={() => onAddProduct('addon')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <RelationTypeCard
            title="Top Competitors"
            relations={top_competitors}
            color="warning"
            relationType="competitor"
            overrideByCandidate={overrideByCandidate}
            onOverride={onOverride}
            onAdd={() => onAddProduct('competitor')}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RelationTypeCard with action menus
// ---------------------------------------------------------------------------

function RelationTypeCard({ title, relations, color, relationType, overrideByCandidate, onOverride, onAdd }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRel, setMenuRel] = useState(null);

  const handleMenuOpen = (e, rel) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRel(rel);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRel(null);
  };

  const handleAction = (decision) => {
    if (menuRel) {
      const candidateId = menuRel.product_id || menuRel.candidate_product_id;
      onOverride(candidateId, decision);
    }
    handleMenuClose();
  };

  const otherTypes = ['complement', 'addon', 'competitor'].filter((t) => t !== relationType);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: TON_ZU_V2[color] ? (t) => v2Tone(t, TON_ZU_V2[color]).main : `${color}.main` }}>
            {title}
          </Typography>
          <Tooltip title={`Manually add a ${relationType}`}>
            <IconButton size="small" onClick={onAdd} color={color}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {relations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No relations found
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {relations.slice(0, 10).map((rel, idx) => {
              const candidateId = rel.product_id || rel.candidate_product_id;
              const override = overrideByCandidate[candidateId];
              const isExcluded = override?.decision === 'exclude';
              const isManual = override && override.decision.startsWith('force_');

              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: isExcluded ? 'error.light' : 'divider',
                    borderRadius: 1,
                    opacity: isExcluded ? 0.5 : 1,
                    bgcolor: isExcluded ? 'error.50' : 'transparent',
                    ...hoverOnly({ bgcolor: isExcluded ? 'error.50' : 'action.hover' }),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ textDecoration: isExcluded ? 'line-through' : 'none', flex: 1 }}
                    >
                      {rel.name || rel.candidate_name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, rel)}
                      aria-label={`Actions for ${rel.name || rel.candidate_name}`}
                      sx={{ ml: 0.5, mt: -0.5, mr: -0.5 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {rel.sku || rel.candidate_sku}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {isManual && (
                        <Chip
                          label="Manual"
                          size="small"
                          color={color}
                          icon={<CheckCircleIcon />}
                          sx={{ height: 18, fontSize: 9 }}
                        />
                      )}
                      {isExcluded && (
                        <Chip
                          label="Excluded"
                          size="small"
                          color="error"
                          icon={<Block />}
                          sx={{ height: 18, fontSize: 9 }}
                        />
                      )}
                      <Chip
                        label={formatPercent(rel.match_score, { digits: 0 })}
                        size="small"
                        color={color}
                        variant="outlined"
                        sx={TON_ZU_V2[color] ? (t) => ({ color: v2Tone(t, TON_ZU_V2[color]).main, borderColor: v2Tone(t, TON_ZU_V2[color]).main }) : undefined}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuRel && overrideByCandidate[menuRel.product_id || menuRel.candidate_product_id] ? (
          <MenuItem onClick={() => handleAction('auto')}>
            <RestartAlt fontSize="small" sx={{ mr: 1 }} />
            Reset to automatic
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => handleAction('exclude')}>
          <Block fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          Exclude from recommendations
        </MenuItem>
        <Divider />
        {otherTypes.map((t) => (
          <MenuItem key={t} onClick={() => handleAction(`force_${t}`)}>
            <SwapHoriz fontSize="small" sx={{ mr: 1 }} />
            Move to {t === 'addon' ? 'Add-ons' : t.charAt(0).toUpperCase() + t.slice(1) + 's'}
          </MenuItem>
        ))}
        <MenuItem onClick={() => handleAction(`force_${relationType}`)}>
          <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
          Pin as {relationType === 'addon' ? 'Add-on' : relationType.charAt(0).toUpperCase() + relationType.slice(1)}
        </MenuItem>
      </Menu>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ManualOverridesSection
// ---------------------------------------------------------------------------

function ManualOverridesSection({ overrides, loading, onReset, saving }) {
  const [expanded, setExpanded] = useState(false);

  const manualOverrides = overrides.filter((o) => o.decision !== 'auto');
  if (manualOverrides.length === 0 && !loading) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: 1 }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Manual Overrides
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <Chip label={manualOverrides.length} size="small" color="primary" />
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {expanded ? 'Hide' : 'Show'}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            These overrides are applied on top of the auto-generated relations. Changes take effect in the storefront after the next nightly batch run.
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Decision</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {manualOverrides.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>
                    <Typography variant="body2">{o.candidate_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{o.candidate_sku}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={DECISION_LABELS[o.decision] || o.decision}
                      size="small"
                      color={DECISION_COLORS[o.decision] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {o.note || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {o.updated_at ? formatDate(o.updated_at) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Reset to automatic">
                      <IconButton
                        size="small"
                        onClick={() => onReset(o.candidate_product_id)}
                        disabled={saving}
                      >
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// AddProductDialog
// ---------------------------------------------------------------------------

function AddProductDialog({ open, relationType, products, anchorProduct, onClose, onSave, saving }) {
  const [candidateProduct, setCandidateProduct] = useState(null);
  const [note, setNote] = useState('');

  const decisionMap = {
    complement: 'force_complement',
    addon: 'force_addon',
    competitor: 'force_competitor',
  };

  const typeLabel = relationType === 'addon' ? 'Add-on' : relationType
    ? relationType.charAt(0).toUpperCase() + relationType.slice(1)
    : '';

  const handleSave = () => {
    if (!candidateProduct) return;
    onSave(candidateProduct.product_id, decisionMap[relationType], note);
    setCandidateProduct(null);
    setNote('');
  };

  const handleClose = () => {
    setCandidateProduct(null);
    setNote('');
    onClose();
  };

  // Filter out the anchor product itself
  const availableProducts = products.filter(
    (p) => p.product_id !== anchorProduct?.product_id
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manually Add {typeLabel}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Pin a product as a <strong>{typeLabel}</strong> for <strong>{anchorProduct?.product_name}</strong>.
          This override takes precedence over the auto-generated relations and will appear in the storefront after the next nightly batch.
        </Typography>

        <Autocomplete
          options={availableProducts}
          getOptionLabel={(option) => `${option.product_name} (${option.sku})`}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase();
            if (!inputValue) return options.slice(0, 50);
            return options
              .filter(
                (o) =>
                  o.product_name?.toLowerCase().includes(inputValue) ||
                  o.sku?.toLowerCase().includes(inputValue)
              )
              .slice(0, 50);
          }}
          value={candidateProduct}
          onChange={(e, val) => setCandidateProduct(val)}
          renderInput={(params) => (
            <TextField {...params} label="Search product" placeholder="Type name or SKU…" size="small" />
          )}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Note (optional)"
          placeholder="e.g. 'Confirmed by support team' or 'Best match for bundle'"
          fullWidth
          size="small"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <DisabledReason
          reason={saving ? null : !candidateProduct ? 'Select a product first.' : null}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!candidateProduct || saving}
            startIcon={saving ? <CircularProgress size={14} /> : <AddCircleOutline />}
          >
            {saving ? 'Saving…' : `Add as ${typeLabel}`}
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ListView
// ---------------------------------------------------------------------------

function ListView({ data, overrideByCandidate, onOverride }) {
  const { top_complements, top_addons, top_competitors } = data;

  const allRelations = [
    ...top_complements.map((r) => ({ ...r, type: 'complement' })),
    ...top_addons.map((r) => ({ ...r, type: 'addon' })),
    ...top_competitors.map((r) => ({ ...r, type: 'competitor' })),
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'complement': return 'success';
      case 'addon': return 'info';
      case 'competitor': return 'warning';
      default: return 'default';
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Product Name</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell align="right">Match Score</TableCell>
            <TableCell>Override</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allRelations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary">
                  No relations found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            allRelations.map((rel, idx) => {
              const candidateId = rel.product_id || rel.candidate_product_id;
              const override = overrideByCandidate?.[candidateId];
              return (
                <TableRow key={idx} hover sx={{ opacity: override?.decision === 'exclude' ? 0.5 : 1 }}>
                  <TableCell>
                    <Chip label={rel.type} size="small" color={getTypeColor(rel.type)} variant="outlined" />
                  </TableCell>
                  <TableCell>{rel.name || rel.candidate_name}</TableCell>
                  <TableCell>{rel.sku || rel.candidate_sku}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={formatPercent(rel.match_score, { digits: 0 })}
                      size="small"
                      color={getTypeColor(rel.type)}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    {override ? (
                      <Chip
                        label={DECISION_LABELS[override.decision] || override.decision}
                        size="small"
                        color={DECISION_COLORS[override.decision] || 'default'}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">auto</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Exclude from recommendations">
                      <IconButton
                        size="small"
                        onClick={() => onOverride(candidateId, override?.decision === 'exclude' ? 'auto' : 'exclude')}
                        color={override?.decision === 'exclude' ? 'error' : 'default'}
                      >
                        <Block fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// HealthStatsCard
// ---------------------------------------------------------------------------

function HealthStatsCard({ stats }) {
  const { coverage, relations_breakdown, quality, gaps } = stats;

  const getCoverageColor = (rate) => {
    if (rate >= 0.8) return 'success.main';
    if (rate >= 0.5) return 'warning.main';
    return 'error.main';
  };

  const getCoverageStatus = (rate) => {
    if (rate >= 0.8) return 'Healthy';
    if (rate >= 0.5) return 'Needs Attention';
    return 'Critical';
  };

  const coverageRate = coverage.coverage_rate;
  const totalGaps = gaps.no_complements + gaps.no_addons + gaps.no_competitors;

  const relationTypeItems = [
    {
      name: 'Complements',
      value: relations_breakdown.by_type.complement,
      label: 'Complement',
      meta: formatPercent(relations_breakdown.by_type.complement / relations_breakdown.total_relations, { digits: 0 }),
    },
    {
      name: 'Add-ons',
      value: relations_breakdown.by_type.addon,
      label: 'Add-on',
      meta: formatPercent(relations_breakdown.by_type.addon / relations_breakdown.total_relations, { digits: 0 }),
    },
    {
      name: 'Competitors',
      value: relations_breakdown.by_type.competitor,
      label: 'Competitor',
      meta: formatPercent(relations_breakdown.by_type.competitor / relations_breakdown.total_relations, { digits: 0 }),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
      <KpiRow>
        <KpiCell>
          <StatTile
            label="Coverage Rate"
            value={formatPercent(coverageRate, { digits: 0 })}
            hint={`${formatInt(coverage.products_with_relations)} of ${formatInt(coverage.total_products)} products`}
            labelTooltip="Percentage of products with at least one relation. Healthy systems have 80%+ coverage. Low coverage may indicate taxonomy gaps or missing canonical types."
            right={
              <Chip
                label={getCoverageStatus(coverageRate)}
                size="small"
                sx={{
                  bgcolor: `${getCoverageColor(coverageRate)}15`,
                  color: getCoverageColor(coverageRate),
                  fontWeight: 600,
                  border: 'none',
                }}
              />
            }
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Total Relations"
            value={formatInt(relations_breakdown.total_relations)}
            hint={`Avg ${relations_breakdown.avg_per_product} per product`}
            labelTooltip="Total number of product relationships across all types (complements, add-ons, competitors). Relations are automatically generated based on taxonomy affinity and compatibility rules."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Manual Overrides"
            value={formatInt(quality.total_overrides)}
            hint={quality.approval_rate !== null
              ? `${formatInt(quality.approval_count)} approved, ${formatInt(quality.rejection_count)} rejected`
              : 'No overrides yet'
            }
            labelTooltip="Number of relations manually curated by admins. Overrides let you approve good auto-suggestions or reject poor ones to improve recommendation quality over time."
          />
        </KpiCell>
        <KpiCell>
          <StatTile
            label="Data Gaps"
            value={formatInt(totalGaps)}
            hint={`${gaps.no_complements} / ${gaps.no_addons} / ${gaps.no_competitors} missing`}
            labelTooltip="Products missing specific relation types (complements / add-ons / competitors). Gaps typically indicate products with incomplete taxonomy data or no matching products in that category."
            right={
              totalGaps > 300 ? (
                <Chip
                  label="Review"
                  size="small"
                  sx={{ bgcolor: 'warning.light', color: 'warning.dark', fontWeight: 600, border: 'none' }}
                />
              ) : null
            }
          />
        </KpiCell>
      </KpiRow>

      <KpiRow>
        <KpiCell>
          <DonutCard
            label="Relation Types Distribution"
            items={relationTypeItems}
            palette={[V2.okText, V2.infoText, V2.warnText]}
            bare
          />
        </KpiCell>
        <KpiCell>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Breakdown by Type
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              {[
                { label: 'Complements', color: V2.okText, value: relations_breakdown.by_type.complement },
                { label: 'Add-ons', color: V2.infoText, value: relations_breakdown.by_type.addon },
                { label: 'Competitors', color: V2.warnText, value: relations_breakdown.by_type.competitor },
              ].map(({ label, color, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="600">{formatInt(value)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </KpiCell>
        <KpiCell>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Missing Relations Coverage
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              {[
                { label: 'Without Complements', value: gaps.no_complements },
                { label: 'Without Add-ons', value: gaps.no_addons },
                { label: 'Without Competitors', value: gaps.no_competitors },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight="600">{formatInt(value)}</Typography>
                  </Box>
                  <Box sx={{ height: 4, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%',
                      width: `${Math.min(100, (value / coverage.total_products) * 100)}%`,
                      bgcolor: value > 100 ? 'error.main' : 'success.main',
                      transition: 'width 0.3s ease',
                    }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </KpiCell>
        <KpiCell>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              System Health
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Products without relations
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color={coverage.products_without_relations > 50 ? 'error.main' : 'text.primary'}
                >
                  {formatInt(coverage.products_without_relations)}
                </Typography>
              </Box>
              {quality.approval_rate !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Override approval rate
                  </Typography>
                  <Typography variant="h6" fontWeight="600">
                    {formatPercent(quality.approval_rate, { digits: 0 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    System-generated recommendations
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </KpiCell>
      </KpiRow>
    </Box>
  );
}
